/**
 * Events (i88-i90): read-only records for HQ staff.
 *
 * Every event a player hosts on a court they booked, and every per-seat session
 * a venue runs, with its roster. Nothing here changes an event: cancelling or
 * removing a player is the host's or the venue's act. Payment is not collected
 * for events in phase 1, so there is no money column to reconcile.
 *
 * API: GET /v1/hq/events, GET /v1/hq/events/:id (booking module, view).
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert, Card, EmptyState, LoadingState, Search, SectionHeader, Select, StatusChip,
  Table, Tbody, Td, Th, Thead, Tr,
} from "@/components/ui";
import type { StatusTone } from "@/components/ui/StatusChip";
import { API_VERSION_PREFIX, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";

type EventStatus = "draft" | "published" | "full" | "started" | "completed" | "cancelled";

interface HqEvent {
  id: string;
  slug: string;
  host_type: "user" | "business";
  status: EventStatus;
  title: string;
  description: string;
  sport: { name: string };
  venue: { id: string; name: string };
  court_name: string;
  starts_at: string;
  ends_at: string;
  timezone_id: string;
  capacity: number;
  confirmed_count: number;
  waitlist_count: number;
  price_amount: string;
  currency: string;
  skill_levels: string[];
  host: { kind: string; name: string | null; handle: string | null };
  cancelled_reason: string | null;
  created_at: string;
  host_user_id?: string;
  booking_id?: string | null;
}

interface HqParticipant {
  user_id: string;
  status: string;
  is_host: boolean;
  name: string | null;
  handle: string | null;
  joined_at: string | null;
  queued_at?: string;
  left_at?: string | null;
  removed_at?: string | null;
}

const TONE: Record<EventStatus, StatusTone> = {
  draft: "neutral",
  published: "success",
  full: "warning",
  started: "info",
  completed: "neutral",
  cancelled: "danger",
};

const LABEL: Record<EventStatus, string> = {
  draft: "Draft",
  published: "Open",
  full: "Full",
  started: "In progress",
  completed: "Finished",
  cancelled: "Cancelled",
};

/** In the venue's own zone, 12-hour. */
function when(iso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toISOString();
  }
}

const PAGE = 50;

export function EventsDirectoryPage() {
  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Bookings", to: "/app/bookings" }, { label: "Events" }]);
  const api = useApiClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [hostType, setHostType] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<HqEvent[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (status) qs.set("status", status);
    if (hostType) qs.set("host_type", hostType);
    if (q.trim()) qs.set("q", q.trim());
    try {
      const body = await api.get<{ events: HqEvent[]; total: number }>(`${API_VERSION_PREFIX}/hq/events?${qs}`);
      setRows(body.events);
      setTotal(body.total);
    } catch (e) {
      setError(apiErrorMessage(e));
      setRows([]);
    }
  }, [api, status, hostType, q, offset]);

  useEffect(() => { void load(); }, [load]);

  return (
    <Stack gap={16}>
      <SectionHeader
        title="Events"
        description="Games players host on courts they booked, and open-play sessions venues run. Read only."
      />
      <Stack gap={8}>
        <Search value={q} onValueChange={(v) => { setOffset(0); setQ(v); }} label="Search by title or venue" />
        <Select label="Status" value={status} onChange={(e) => { setOffset(0); setStatus(e.target.value); }}
          options={[{ value: "", label: "Any" }, { value: "published", label: "Published" }, { value: "draft", label: "Draft" }, { value: "cancelled", label: "Cancelled" }]} />
        <Select label="Host" value={hostType} onChange={(e) => { setOffset(0); setHostType(e.target.value); }}
          options={[{ value: "", label: "Any" }, { value: "user", label: "Player hosted" }, { value: "business", label: "Venue event" }]} />
      </Stack>
      {error ? <Alert tone="danger" title="Could not load events">{error}</Alert> : null}
      {rows === null ? <LoadingState message="Loading events" /> : rows.length === 0 ? (
        <EmptyState reason={status || hostType || q ? "filters" : "none"}
          description={status || hostType || q ? undefined : "No events yet. They appear when a player hosts one from a paid booking or a venue runs a session."} />
      ) : (
        <>
          <Table>
            <Thead>
              <Tr><Th>Event</Th><Th>When (venue time)</Th><Th>Venue</Th><Th>Host</Th><Th>Players</Th><Th>Status</Th></Tr>
            </Thead>
            <Tbody>
              {rows.map((e) => (
                <Tr key={e.id} onClick={() => navigate(`/app/bookings/events/${e.id}`)} style={{ cursor: "pointer" }}>
                  <Td><Link to={`/app/bookings/events/${e.id}`}>{e.title}</Link><br /><Text role="caption" color="muted" as="span">{e.sport.name}</Text></Td>
                  <Td>{when(e.starts_at, e.timezone_id)}</Td>
                  <Td>{e.venue.name}, {e.court_name}</Td>
                  <Td>{e.host_type === "business" ? "Venue" : (e.host.name ?? "Player")}</Td>
                  <Td>{e.confirmed_count} of {e.capacity}{e.waitlist_count ? `, ${e.waitlist_count} waiting` : ""}</Td>
                  <Td><StatusChip tone={TONE[e.status]}>{LABEL[e.status]}</StatusChip></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Text role="small" color="muted">
            {offset + 1} to {offset + rows.length} of {total}{" "}
            {offset > 0 ? <button type="button" onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button> : null}{" "}
            {offset + rows.length < total ? <button type="button" onClick={() => setOffset(offset + PAGE)}>Next</button> : null}
          </Text>
        </>
      )}
    </Stack>
  );
}

export function EventDetailPage() {
  const { eventId = "" } = useParams();
  const api = useApiClient();
  const [data, setData] = useState<{ event: HqEvent; participants: HqParticipant[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" }, { label: "Bookings", to: "/app/bookings" },
    { label: "Events", to: "/app/bookings/events" }, { label: data?.event.title ?? "Event" },
  ]);

  useEffect(() => {
    let alive = true;
    api.get<{ event: HqEvent; participants: HqParticipant[] }>(`${API_VERSION_PREFIX}/hq/events/${encodeURIComponent(eventId)}`)
      .then((b) => { if (alive) setData(b); })
      .catch((e) => { if (alive) setError(apiErrorMessage(e)); });
    return () => { alive = false; };
  }, [api, eventId]);

  if (error) return <Alert tone="danger" title="Could not load this event">{error}</Alert>;
  if (!data) return <LoadingState message="Loading event" />;
  const e = data.event;
  const group = (s: string) => data.participants.filter((p) => p.status === s);

  return (
    <Stack gap={16}>
      <SectionHeader title={e.title} description={`${e.sport.name} at ${e.venue.name}, ${e.court_name}`}
        actions={<StatusChip tone={TONE[e.status]}>{LABEL[e.status]}</StatusChip>} />
      <Card>
        <Stack gap={8}>
          <Text>When: {when(e.starts_at, e.timezone_id)} to {when(e.ends_at, e.timezone_id)} ({e.timezone_id})</Text>
          <Text>Host: {e.host_type === "business" ? `Venue (${e.venue.name})` : `${e.host.name ?? "Player"}${e.host.handle ? ` @${e.host.handle}` : ""}`}</Text>
          {e.host_user_id ? <Text role="small" color="muted">Created by user {e.host_user_id}</Text> : null}
          {e.booking_id ? <Text role="small" color="muted">Anchored to booking {e.booking_id}</Text> : null}
          <Text>Players: {e.confirmed_count} of {e.capacity}, waitlist {e.waitlist_count}</Text>
          <Text>Price shown: {Number(e.price_amount) > 0 ? `${e.currency} ${e.price_amount} per player (paid directly, not through HUDDL)` : "Free"}</Text>
          <Text>Skill levels: {e.skill_levels.join(", ")}</Text>
          {e.cancelled_reason ? <Text>Cancelled: {e.cancelled_reason.replace(/_/g, " ")}</Text> : null}
          {e.description ? <Text color="secondary">{e.description}</Text> : null}
          <Text role="small" color="muted">Share link: /e/{e.slug}</Text>
        </Stack>
      </Card>
      {(["confirmed", "waitlisted", "left", "removed"] as const).map((s) => (
        <Card key={s} header={<Text role="title">{s === "confirmed" ? "Confirmed" : s === "waitlisted" ? "Waitlist" : s === "left" ? "Left" : "Removed"} ({group(s).length})</Text>}>
          {group(s).length === 0 ? <Text color="muted">Nobody.</Text> : (
            <Table>
              <Thead><Tr><Th>Player</Th><Th>User id</Th><Th>Since</Th></Tr></Thead>
              <Tbody>
                {group(s).map((p) => (
                  <Tr key={p.user_id}>
                    <Td>{p.name ?? "Player"}{p.handle ? ` @${p.handle}` : ""}{p.is_host ? " (host)" : ""}</Td>
                    <Td><Link to={`/app/users/${p.user_id}`}>{p.user_id}</Link></Td>
                    <Td>{when(p.removed_at ?? p.left_at ?? p.joined_at ?? p.queued_at ?? e.created_at, e.timezone_id)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Card>
      ))}
    </Stack>
  );
}
